import Long from 'long';

import {
  IMCChatPreview,
  IMCMessage,
  IMCMethodSearchChatsResponse,
  MCMethod,
  MCMethodSearchChats,
  MCMethodSearchMessages,
  api,
} from '../../api/proto';
import {equalUint8Arrays, randomUint8Array, uint8ArrayToBase64, uuidToUint8Array} from '../../utils/arrayUtils';
import LRU from '../../utils/lru';
import {ChannelsUpdaterEvent} from '../Channel/ChannelsUpdater';
import RawChatsStore from '../RawChatsStore';
import {IRawChat} from '../RawChatsStore/RawChat';
import RawChatsStoreMode from '../RawChatsStore/RawChatsStoreMode';
import {WorkspaceStore} from '../Workspaces';
import RawChatsSearchEvent from './RawChatsSearchEvent';

type SearchChatsCacheEntry = {
  rawChats: IRawChat[];
};

type SearchMessagesCacheEntry = {
  rawMessages: IMCMessage[];
  rawChats: IMCChatPreview[];
};

export class RawChatsSearch extends RawChatsStore {
  private static readonly CACHE_TTL_MS = 60_000;
  private static readonly MESSAGES_CACHE_TTL_MS = 60_000;
  private static readonly MAX_CACHE_ENTRIES = 50;

  private searchChatsCache_ = new LRU<string, SearchChatsCacheEntry>({
    maxSize: RawChatsSearch.MAX_CACHE_ENTRIES,
    maxAge: RawChatsSearch.CACHE_TTL_MS,
  });

  private searchMessagesCache_ = new LRU<string, SearchMessagesCacheEntry>({
    maxSize: RawChatsSearch.MAX_CACHE_ENTRIES,
    maxAge: RawChatsSearch.MESSAGES_CACHE_TTL_MS,
  });


  protected isInit_: boolean = false;

  public channelIDs: Uint8Array[] = [];
  protected channelID?: Uint8Array | null = null;

  constructor(protected workspace: WorkspaceStore) {
    super(workspace, [], '', RawChatsStoreMode.SEARCH);

    this.workspace.updater.on(ChannelsUpdaterEvent.UPDATE, this.onWorkspaceDataChange_);
    this.workspace.updater.on(ChannelsUpdaterEvent.REFRESH_CHAT, this.onWorkspaceDataChange_);
    this.workspace.updater.on(ChannelsUpdaterEvent.NEW_OUTGOING_MESSAGE, this.onWorkspaceDataChange_);
  }

  public destroy = () => {
    this.workspace.updater.off(ChannelsUpdaterEvent.UPDATE, this.onWorkspaceDataChange_);
    this.workspace.updater.off(ChannelsUpdaterEvent.REFRESH_CHAT, this.onWorkspaceDataChange_);
    this.workspace.updater.off(ChannelsUpdaterEvent.NEW_OUTGOING_MESSAGE, this.onWorkspaceDataChange_);

    RawChatsStore.prototype.destroy.call(this);
  };

  public clear = () => {
    this.invalidateCache();
    this.isInit_ = false;
    this.channelIDs = [];
    this.storeKey = '';
    this.rawChats_ = [];
    this.chatsIdNumbersDict_ = {};
    this.emit(RawChatsSearchEvent.CLEAR, this);
  };

  protected setChats_ = (rawChats: IRawChat[], channelIDs: Uint8Array[]) => {
    this.isInit_ = true;
    this.rawChats_ = rawChats;
    this.channelIDs = channelIDs;
    this.storeKey = channelIDs.map((id) => uint8ArrayToBase64(id)).join('_');

    const chatsIdNumbersDict: {[id: string]: boolean} = {};
    rawChats.forEach((raw) => {
      chatsIdNumbersDict[raw.id?.toString() || ''] = true;
    });
    this.chatsIdNumbersDict_ = chatsIdNumbersDict;
  };

  public find = (chatID?: Long | null): IRawChat | null => {
    return this.rawChats_.find((raw) => chatID && raw.id?.equals(chatID)) || null;
  };

  public searchChatsAndMessages = async (query: string, chatID?: Long | null, channelID?: Uint8Array | null) => {
    await Promise.all([
      this.searchChats(query, channelID),
      this.searchMessages(query, chatID, channelID),
    ]);
  };

  private searchChatsTrx_: Uint8Array | null = null;

  private onWorkspaceDataChange_ = () => {
    this.invalidateCache();
  };

  private getCacheKey_ = (query: string, channelID?: Uint8Array | null): string => {
    const workspaceKey = this.workspace.id?.toString?.() || '';
    const channelKey = channelID ? uint8ArrayToBase64(channelID) : '';

    return `${workspaceKey}_${channelKey}_${query.toLowerCase()}`;
  };

  private getMessagesCacheKey_ = (query: string, chatID?: Long | null, channelID?: Uint8Array | null): string => {
    const workspaceKey = this.workspace.id?.toString?.() || '';
    const channelKey = channelID ? uint8ArrayToBase64(channelID) : '';
    const chatKey = chatID?.toString() || '';

    return `${workspaceKey}_${channelKey}_${chatKey}_${query.toLowerCase()}`;
  };

  public invalidateCache = () => {
    this.searchChatsCache_.clear();
    this.searchMessagesCache_.clear();
  };

  public searchChats = async (query: string, channelID?: Uint8Array | null) => {
    if (!query) {
      this.clear();
      return;
    }

    const cacheKey = this.getCacheKey_(query, channelID);
    const cachedChats = this.searchChatsCache_.get(cacheKey);
    if (cachedChats) {
      this.processSearchChats_({chats: cachedChats.rawChats}, channelID);
      return;
    }

    const req = new MCMethodSearchChats({
      channelID,
      query,
    });

    this.searchChatsTrx_ = randomUint8Array();

    const {error, res, trx} = await this.workspace.request<api.ChannelsResponse>(
      {
        channelsRequest: new api.ChannelsRequest({
          workspaceId: this.workspace.id,
          action: new MCMethod({
            searchChats: req,
          }),
        }),
      },
      'channelsResponse',
      {trx: this.searchChatsTrx_},
    );

    if (equalUint8Arrays(this.searchChatsTrx_, uuidToUint8Array(trx)) && !error) {
      const searchChatsRes = res?.methodResponse?.searchChats;
      this.searchChatsCache_.set(cacheKey, {rawChats: searchChatsRes?.chats || []});
      this.processSearchChats_(searchChatsRes, channelID);
    }
  };

  private processSearchChats_ = (res?: IMCMethodSearchChatsResponse | null, channelID?: Uint8Array | null) => {
    const rawChats = res?.chats || [];
    this.setChats_(rawChats, channelID ? [channelID] : []);
    console.debug(
      `%c----->RawChatsSearch->processSearchChats_ ${this.storeKey} mode: ${this.mode}`,
      'color: gray',
      this.chatsIdNumbersDict_,
      rawChats,
    );

    this.emit(RawChatsSearchEvent.FIND_CHATS, this.rawChats_, channelID);
  };

  private searchMessagesTrx_: Uint8Array | null = null;

  public searchMessages = async (query: string, chatID?: Long | null, channelID?: Uint8Array | null) => {
    if (!query) {
      this.clear();
      return;
    }

    const cacheKey = this.getMessagesCacheKey_(query, chatID, channelID);
    const cachedMessages = this.searchMessagesCache_.get(cacheKey);
    if (cachedMessages) {
      this.processSearchChatsMessagesData_(cachedMessages.rawMessages, cachedMessages.rawChats, channelID);
      return;
    }

    const req = new MCMethodSearchMessages({
      channelID,
      chatID,
      query,
    });

    this.searchMessagesTrx_ = randomUint8Array();

    const {error, res, trx} = await this.workspace.request<api.ChannelsResponse>(
      {
        channelsRequest: new api.ChannelsRequest({
          workspaceId: this.workspace.id,
          action: new MCMethod({
            searchMessages: req,
          }),
        }),
      },
      'channelsResponse',
      {trx: this.searchMessagesTrx_},
    );

    if (equalUint8Arrays(this.searchMessagesTrx_, uuidToUint8Array(trx)) && !error) {
      const rawMessages = res?.methodResponse?.searchMessages?.messages || [];
      const rawChats = res?.methodResponse?.searchMessages?.mentionedChats || [];

      this.searchMessagesCache_.set(cacheKey, {rawMessages, rawChats});
      this.processSearchChatsMessages_(res, channelID);
    }
  };

  private processSearchChatsMessagesData_ = (
    rawMessages: IMCMessage[],
    rawChats: IMCChatPreview[],
    channelID?: Uint8Array | null,
  ) => {
    this.emit(RawChatsSearchEvent.FIND_MESSAGES, rawMessages, channelID, rawChats);
  };

  private processSearchChatsMessages_ = (res?: api.ChannelsResponse | null, channelID?: Uint8Array | null) => {
    const rawMessages = res?.methodResponse?.searchMessages?.messages || [];
    const rawChats = res?.methodResponse?.searchMessages?.mentionedChats || [];

    this.processSearchChatsMessagesData_(rawMessages, rawChats, channelID);
  };
}

export default RawChatsSearch;